import React from "react";

const normalizeHref = (raw) => {
  const v = (raw || "").trim();
  if (!v) return "";

  const phoneLike = /^\+?\d[\d\s-]{6,}$/.test(v);
  if (phoneLike) {
    const digits = v.replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  if (v.toLowerCase().startsWith("wa:") || v.toLowerCase().startsWith("whatsapp:")) {
    const digits = v.split(":").slice(1).join(":").replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : "";
  }

  if (/^https?:\/\//i.test(v)) return v;
  if (v.toLowerCase().startsWith("www.")) return `https://${v}`;
  if (v.startsWith("/")) return v;
  return `/${v}`;
};

const goToLink = (raw) => {
  const href = normalizeHref(raw);
  if (!href) return;

  const isExternal = /^https?:\/\//i.test(href);
  if (isExternal) window.open(href, "_blank", "noopener,noreferrer");
  else window.location.href = href;
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const HeroSection3 = ({ config, style }) => {
  const direction = (config?.direction || "ltr").toLowerCase();
  const isRTL = direction === "rtl";

  const align = (config?.alignment || "center").toLowerCase();
  const alignMap = { left: "text-left", center: "text-center", right: "text-right" };
  const justifyMap = { left: "justify-start", center: "justify-center", right: "justify-end" };

  const alignClass = alignMap[align] || "text-center";
  const justifyClass = justifyMap[align] || "justify-center";

  const subtitleWrapClass =
    align === "center" ? "mx-auto" : align === "right" ? "ml-auto" : "mr-auto";

  const statsWrapClass =
    align === "center" ? "mx-auto" : align === "right" ? "ml-auto" : "mr-auto";

  const stats = Array.isArray(config?.stats) ? config.stats : [];

  const primaryText = (config?.buttonText || "").trim();
  const primaryLink = (config?.buttonLink || "").trim();
  const primaryDisabled = !!primaryText && !primaryLink;

  const secondaryText = (config?.secondaryButtonText || "").trim();
  const secondaryLink = (config?.secondaryButtonLink || "").trim();
  const secondaryDisabled = !!secondaryText && !secondaryLink;

  const bg = buildBg(style, "bg-white");

  return (
    <section
      dir={direction}
      style={bg.style}
      className={`min-h-[90vh] flex items-center py-20 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-6xl w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          {/* Text */}
          <div className={alignClass}>
            <h1
              style={{ color: style?.titleColor || undefined }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-8"
            >
              {config?.title || "Your title"}
            </h1>

            <p
              style={{ color: style?.subtitleColor || undefined }}
              className={`text-xl text-gray-600 mb-12 max-w-3xl ${subtitleWrapClass}`}
            >
              {config?.subtitle || "Your subtitle"}
            </p>

            {primaryText || secondaryText ? (
              <div className={`flex flex-wrap gap-3 ${justifyClass} mb-12`}>
                {primaryText ? (
                  <button
                    disabled={primaryDisabled}
                    onClick={() => !primaryDisabled && goToLink(primaryLink)}
                    style={{
                      backgroundColor: style?.primaryBtnBg || undefined,
                      color: style?.primaryBtnText || undefined,
                      borderColor: style?.primaryBtnBg || undefined,
                    }}
                    className={`border-2 font-semibold py-4 px-10 rounded-lg transition-all duration-300 text-lg ${
                      primaryDisabled
                        ? "opacity-40 cursor-not-allowed"
                        : style?.primaryBtnBg
                        ? "hover:opacity-90"
                        : "bg-transparent border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                    }`}
                  >
                    {primaryText}
                  </button>
                ) : null}

                {secondaryText ? (
                  <button
                    disabled={secondaryDisabled}
                    onClick={() => !secondaryDisabled && goToLink(secondaryLink)}
                    style={{
                      borderColor: style?.secondaryBtnBorder || undefined,
                      color: style?.secondaryBtnText || undefined,
                    }}
                    className={`border-2 font-semibold py-4 px-10 rounded-lg transition-all duration-300 text-lg ${
                      secondaryDisabled
                        ? "opacity-40 cursor-not-allowed"
                        : style?.secondaryBtnBorder || style?.secondaryBtnText
                        ? "hover:opacity-90"
                        : "bg-transparent border-gray-300 text-gray-800 hover:bg-gray-50"
                    }`}
                  >
                    {secondaryText}
                  </button>
                ) : null}
              </div>
            ) : null}

            {stats.length > 0 ? (
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl ${statsWrapClass}`}>
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: style?.statsCardBg || undefined,
                      borderColor: style?.statsCardBorder || undefined,
                    }}
                    className={`text-center p-6 rounded-2xl transition-shadow ${
                      style?.statsCardBg || style?.statsCardBorder
                        ? "border"
                        : "bg-gray-50 hover:shadow-lg"
                    }`}
                  >
                    <div
                      style={{ color: style?.statsValueColor || undefined }}
                      className="text-4xl font-bold text-blue-600 mb-2"
                    >
                      {stat.value}
                    </div>
                    <div
                      style={{ color: style?.statsLabelColor || undefined }}
                      className="text-gray-600 font-medium"
                    >
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* Image */}
          <div className="relative">
            {config?.image ? (
              <img
                src={config.image}
                alt={config.imageAlt || "Hero"}
                className="rounded-3xl shadow-2xl border border-gray-200 w-full h-auto object-cover"
              />
            ) : (
              <div className="rounded-3xl border border-gray-200 w-full h-[340px] bg-gray-100" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection3;